/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EntityType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface EntityCapabilityProps {
  id: string;
  capabilityId: string;
  entityId: string;
  entityType: EntityType;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class EntityCapabilityRecord extends Record<EntityCapabilityProps>({
  id: null as any,
  capabilityId: null as any,
  entityId: null as any,
  entityType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    capabilityId: string;
    entityId: string;
    entityType: EntityType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): EntityCapabilityRecord {
    if (json == null) {
      throw new Error('EntityCapabilityRecord.fromJS: parameter cannot be null');
    }
    return new EntityCapabilityRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'EntityCapabilityJson.id',
      ),
      capabilityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'capabilityId', '00000000-0000-0000-0000-000000000000'),
        'EntityCapabilityJson.capabilityId',
      ),
      entityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
        'EntityCapabilityJson.entityId',
      ),
      entityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
        'EntityCapabilityJson.entityType',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    capabilityId: string;
    entityId: string;
    entityType: EntityType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [EntityCapabilityRecord, Error[]] {
    if (json == null) {
      throw new Error('EntityCapabilityRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new EntityCapabilityRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'EntityCapabilityJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      capabilityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'capabilityId', '00000000-0000-0000-0000-000000000000'),
            'EntityCapabilityJson.capabilityId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
            'EntityCapabilityJson.entityId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
            'EntityCapabilityJson.entityType',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: EntityCapabilityProps) {
    super(props);
    if (props == null) {
      throw new Error('EntityCapabilityRecord constructor parameter cannot be null');
    }
  }
}

export abstract class EntityCapabilityViewModelBase implements EntityCapabilityProps {
  protected readonly _props: EntityCapabilityProps;
  private _json?: EntityCapabilityProps;

  get id(): string {
    return this._props.id;
  }
  get capabilityId(): string {
    return this._props.capabilityId;
  }
  get entityId(): string {
    return this._props.entityId;
  }
  get entityType(): EntityType {
    return this._props.entityType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }

  public isAnyEntityType(...entityType: EntityType[]) {
    return entityType.includes(this._props.entityType);
  }

  constructor(props: EntityCapabilityProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        capabilityId: this._props.capabilityId,
        entityId: this._props.entityId,
        entityType: this._props.entityType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllEntityCapabilityEntities = (state: AppState) =>
  state.entityStores.entityCapability.entities;

export const selectEntityCapabilityById = (id: string) => (state: AppState) =>
  state.entityStores.entityCapability.entities.get(id);

export const selectEntityCapabilityMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.entityCapability.meta.get(id);

export const selectEntityCapabilityAndMetadataById = (id: string) =>
  createSelector(
    selectEntityCapabilityById(id),
    selectEntityCapabilityMetadataById(id),
    (entityCapability, meta) => ({ entityCapability, meta }),
  );

export function selectEntityCapabilityEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllEntityCapabilityEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, EntityCapabilityRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectEntityCapabilityEntitiesByCapabilityId(capabilityId: string) {
  return createSelector(selectAllEntityCapabilityEntities, (entities) =>
    entities.filter((e) => e.capabilityId === capabilityId).valueSeq(),
  );
}

export function selectEntityCapabilityEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllEntityCapabilityEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectEntityCapabilityEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllEntityCapabilityEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}
//#endregion Selectors
